import { classifyLine, splitTimestamp, LEVEL_STYLES } from "../lib/logLevel";
import { cx } from "./ui";

/**
 * The log pane: one row per line, a timestamp gutter, and failures picked out.
 *
 * Rendering rows rather than one string is what makes both the colour and the
 * gutter possible — and the reason there is a cap. A build that printed fifty
 * thousand lines would otherwise be fifty thousand DOM nodes inside a box
 * showing thirty of them.
 *
 * Colour is emphasis, never the only signal: the level is read *out of* the
 * line, so the word that made it red is still sitting there in the text. That
 * matters for anyone who cannot separate the hues, and is why there is no dot
 * here the way there is on a status badge.
 */
const MAX_RENDERED = 5000;

export function LogLines({ lines, wrap }: { lines: string[]; wrap: boolean }) {
  const overflowing = lines.length > MAX_RENDERED;
  const rendered = overflowing ? lines.slice(-MAX_RENDERED) : lines;

  /*
   * The gutter is only drawn when the log actually carries timestamps. A
   * container that prints none would otherwise get a column of empty space
   * down its left edge for no reason.
   */
  const timestamped = rendered.some((line) => splitTimestamp(line).time !== null);

  return (
    <div className="min-w-full font-mono text-xs leading-5">
      {overflowing && (
        <p className="px-4 py-2 text-fg-subtle italic">
          {lines.length - MAX_RENDERED} earlier lines not shown — download for the whole log.
        </p>
      )}

      {rendered.map((line, index) => {
        const level = classifyLine(line);
        const { time, text } = splitTimestamp(line);

        return (
          <div
            key={index}
            className={cx(
              "flex gap-3 px-4 transition-colors hover:bg-raised",
              level && LEVEL_STYLES[level],
            )}
          >
            {timestamped && (
              <span className="shrink-0 text-fg-subtle select-none tabular-nums">
                {time ?? "        "}
              </span>
            )}
            <span className={cx("min-w-0", wrap ? "break-words whitespace-pre-wrap" : "whitespace-pre")}>
              {/* A zero-width space keeps a blank line one row tall rather
                  than collapsing it, so the output matches the source. */}
              {text === "" ? "​" : text}
            </span>
          </div>
        );
      })}
    </div>
  );
}
